import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, unref as _unref, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 2 }
const _hoisted_2 = { class: "substep-content" }
const _hoisted_3 = { class: "repayment-summary-wrapper-mobile" }
const _hoisted_4 = { class: "sections" }
const _hoisted_5 = { class: "substep-footer" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 0,
  class: "disclaimer mobile"
}

import { ref, computed, onMounted, watch, onUnmounted, nextTick } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/shared/Modal.vue";
import { useRoute, useRouter } from "vue-router";
import RepaymentSummary from "@/components/professional-grad/loan-configuration/RepaymentSummary.vue";
import RepaymentTermSelection from "@/components/professional-grad/loan-configuration/RepaymentTermSelection.vue";
import RateTypeSelection from "@/components/professional-grad/loan-configuration/RateTypeSelection.vue";
import RepaymentTypeSelection from "@/components/professional-grad/loan-configuration/RepaymentTypeSelection.vue";
import Confirmation from "@/components/professional-grad/loan-configuration/Confirmation.vue";
import useApplicationRatePlans from "@/composables/useApplicationRatePlans";
import useApplicationData from "@/composables/useApplicationData";
import {
  RatePlanVM,
  RatePlanRepaymentVM,
  RepaymentTypeOption,
  RateTypeSection,
  RepaymentSummaryVM,
  RepaymentTermOption,
} from "@/models/loanConfiguration";
import { $api } from "@/services/api";
import { ButtonTypes } from "@/models/modal";
import useCustomDialogueModal from "@/composables/useCustomDialogueModal";
import { resolveMergeFields } from "@/use/formatter";
import CustomDialogueModal from "@/components/modals/CustomDialogueModal.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import { AppLoanConfigRequest } from "@/models/api";
import { RateTypeIds } from "@/config/constants";


export default /*@__PURE__*/_defineComponent({
  __name: 'PgLoanOptionsModal',
  props: {
    closeBtn: { type: Boolean, default: true }
  },
  emits: ["close", "configurationComplete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useStore();
const route = useRoute();
const router = useRouter();

const props = __props;

const emit = __emit;

const modalRef = ref<InstanceType<typeof Modal> | null>();
const rateTypeSelectionRef = ref<InstanceType<typeof RateTypeSelection> | null>();
const repaymentTermSelectionRef = ref<InstanceType<typeof RepaymentTermSelection> | null>();
const repaymentTypeSelectionRef = ref<InstanceType<typeof RepaymentTypeSelection> | null>();
const repaymentSummaryMobileRef = ref<InstanceType<typeof RepaymentSummary> | null>();
const repaymentSummaryRef = ref<InstanceType<typeof RepaymentSummary> | null>();
const confirmationRef = ref<InstanceType<typeof Confirmation> | null>();

const { appData, emailAddress, hrefTFN, tollFreeNumber } = useApplicationData();

const {
  error,
  gettingRatePlans,
  loanConfigurationModule,
  previouslySelectedRatePlan,
  previouslySelectedRepaymentOption,
  fixedRatePlans,
  variableRatePlans,
  getRatePlansAsync,
  setSelectedRepaymentOption,
  setSelectedRateTypeId,
  setSelectedRatePlan,
} = useApplicationRatePlans(store);

const { dialogueModalOptions, showDialogueModal, openDialogueModal, closeDialogueModal } =
  useCustomDialogueModal();

const activeStep = ref<number>(1);
const loading = ref<boolean>(true);
const showPreviousSelections = ref<boolean>(false);
const stepMaxWidth = ref<string>("959px");

const changingStep = ref<boolean>(false);
const savingChanges = ref<boolean>(false);
const preserveSelection = ref<boolean>(false);

const isMobile = computed<boolean>(() => modalRef.value?.isMobile || false);
const isTablet = computed<boolean>(() => modalRef.value?.isTablet || false);
const useMobileSizing = computed(() => isMobile.value || isTablet.value);

const showCircleLoader = computed<boolean>(() => {
  return (
    (activeStep.value > 1 && rateTypeSelectionRef.value?.rateTypes?.length === 0) ||
    (activeStep.value > 1 && store.state.loanConfigurationModule.loading)
  );
});

const isNextButtonDisabled = computed<boolean>(() => {
  switch (activeStep.value) {
    case 2:
      return (
        selectedRateTypeId.value === 0 ||
        selectedRateTypeId.value === undefined ||
        changingStep.value
      );
    case 3:
      return (
        selectedRepaymentTermId.value === 0 ||
        selectedRepaymentTermId.value === undefined ||
        changingStep.value
      );
    case 4:
      return (
        selectedRepaymentTypeId.value === 0 ||
        selectedRepaymentTypeId.value === undefined ||
        changingStep.value
      );
    case 5:
    default:
      return false;
  }
});

const stepMinHeight = computed(() => {
  switch (activeStep.value) {
    case 1:
      return "fit-content";
    case 2:
      return "277px";
    case 3:
      return "278px";
    case 4:
      return "485px";

    default:
      return "250px";
  }
});

const styleOptions = computed(() => {
  return {
    modalContainer: {
      maxWidth: stepMaxWidth.value,
    },
    modalBody: {
      minHeight: stepMinHeight.value,
    },
  };
});

const selectedRateTypeId = computed<number | undefined>(
  () => rateTypeSelectionRef.value?.selectedRateTypeId,
);

const selectedRepaymentTermId = computed<number | undefined>(
  () => repaymentTermSelectionRef.value?.selectedRepaymentTermId,
);

const selectedRepaymentTypeId = computed<number | undefined>(
  () => repaymentTypeSelectionRef.value?.selectedRepaymentTypeId,
);

const selectedRepaymentTermOption = computed<RepaymentTermOption | undefined>(() => {
  return repaymentTermSelectionRef.value?.selectedRepaymentTermOption;
});

const selectedRateType = computed<RateTypeSection>(() => {
  return rateTypeSelectionRef.value?.selectedRateType;
});

const selectedRepaymentTerm = computed(() => {
  return repaymentTermSelectionRef.value?.repaymentTermOption;
});

const selectedRepaymentType = computed<RepaymentTypeOption | undefined>(() => {
  return repaymentTypeSelectionRef.value?.selectedRepaymentType;
});

const selectedRatePlan = computed<RatePlanVM | undefined>(() => {
  return repaymentTypeSelectionRef.value?.selectedRatePlan;
});

const ratePlanSelection = computed<RatePlanVM | undefined>(() => {
  return findRatePlan(selectedRateTypeId.value ?? 0, selectedRepaymentType.value?.ratePlanId ?? 0);
});

const loanConfigurationRequest = computed<AppLoanConfigRequest>(() => {
  return {
    applicationId: store.state.appData?.application?.id ?? 0,
    selectedRatePlanId: selectedRepaymentType.value?.ratePlanId ?? 0,
    selectedByApplicantId: store.state.appData?.currentApplicant?.id ?? 0,
    repaymentTypeId: selectedRepaymentTypeId.value ?? 0,
  };
});

const hasLoanConfigurationChanges = computed<boolean>(() => {
  // Rate Plan Changed
  // Repayment Type Id changed

  const currentLoanConfig = appData.value?.application?.loanConfiguration;

  if (
    currentLoanConfig === null ||
    currentLoanConfig?.ratePlanId !== selectedRepaymentType.value?.ratePlanId ||
    currentLoanConfig?.repaymentTypeId !== selectedRepaymentType.value?.repaymentTypeId
  ) {
    return true;
  }

  return false;
});

const repaymentSummary = ref<RepaymentSummaryVM>({
  rateTypeId: 0,
  rateType: "",
  repaymentTerm: "",
  repaymentTermId: 0,
  repaymentType: "",
  repaymentTypeId: 0,
  interestRate: "",
});

function findRatePlan(rateTypeId: number, ratePlanId: number) {
  if (rateTypeId === RateTypeIds.FIXED) {
    return fixedRatePlans.value?.find(
      (ratePlan: RatePlanVM) => ratePlan.rateTypeId === rateTypeId && ratePlan.id === ratePlanId,
    );
  }

  if (rateTypeId === RateTypeIds.VARIABLE) {
    return variableRatePlans.value?.find(
      (ratePlan: RatePlanVM) => ratePlan.rateTypeId === rateTypeId && ratePlan.id === ratePlanId,
    );
  }

  return undefined;
}

async function complete() {
  store.dispatch("loanConfigurationModule/setSavingChanges", true);

  savingChanges.value = true;
  confirmationRef.value?.setSavingChanges(true);

  if (hasLoanConfigurationChanges.value) {
    try {
      const response = await updateLoanConfigurationAsync();

      if (response?.applicationId && response?.applicationId > 0) {
        store.dispatch("setLoanConfiguration", response); // LOC
        store.dispatch("loanConfigurationModule/setSelectedRatePlan", ratePlanSelection.value);
        store.dispatch(
          "loanConfigurationModule/setSelectedRepaymentOption",
          selectedRepaymentType.value,
        );
        store.dispatch("loanConfigurationModule/setLoanConfiguration", response.value);

        emit("configurationComplete");
      } else {
        store.dispatch("loanConfigurationModule/setSavingChanges", false);
        onError();
      }
    } catch (error) {
      console.log("an error occurred");
      store.dispatch("loanConfigurationModule/setSavingChanges", false);
      onError();
    }
  } else {
    emit("configurationComplete");
  }
}

async function updateLoanConfigurationAsync() {
  const response = await $api.applications.addOrUpdateAppLoanConfig(loanConfigurationRequest.value);
  return response;
}

function closeModal() {
  dialogueModalOptions.value = {
    closeBtn: true,
    title: "Are you sure you want to exit?",
    description: "Any selections you've made so far for your rate and repayment will not be saved.",
    buttons: [
      {
        label: "Cancel",
        type: Number(ButtonTypes.Cancel),
        classStyle: "cancel-btn",
        onClick: () => {
          showDialogueModal.value = false;
        },
      },
      {
        label: "Exit",
        type: Number(ButtonTypes.Cancel),
        classStyle: "exit-btn",
        onClick: () => {
          resetStep();

          showDialogueModal.value = false;
          emit("close");
        },
      },
    ],
  };

  openDialogueModal(dialogueModalOptions.value);
}

function resetStep() {
  activeStep.value = 1;

  repaymentSummary.value = {
    rateTypeId: 0,
    rateType: "",
    repaymentTerm: "",
    repaymentTermId: 0,
    repaymentTypeId: 0,
    repaymentType: "",
    interestRate: "",
  };

  rateTypeSelectionRef.value?.resetSelectedRateType();
  repaymentTermSelectionRef.value?.resetSelectedRepaymentTerm();
  repaymentTypeSelectionRef.value?.resetSelectedRepaymentType();
}

function goBack() {
  activeStep.value -= 1;
}

function next() {
  changingStep.value = true;
  switch (activeStep.value) {
    case 2:
      repaymentSummary.value.rateType = selectedRateType.value?.rateTypeName || "";
      repaymentSummary.value.rateTypeId = selectedRateType.value?.rateTypeId || 0;

      setSelectedRateTypeId(repaymentSummary.value.rateTypeId);

      // If a repayment term has already been selected
      if (
        selectedRepaymentTermOption.value &&
        selectedRepaymentTermOption.value?.repaymentTermId > 0
      ) {
        updateRepaymentTerm(selectedRepaymentTermOption.value?.repaymentTermId);
      }

      break;
    case 3:
      updateRepaymentTerm(selectedRepaymentTermOption.value?.repaymentTermId || 0);

      break;
    case 4:
      repaymentSummary.value.repaymentType = selectedRepaymentType.value?.repaymentTypeName || "";
      setSelectedRepaymentOption(selectedRepaymentType.value);
      break;
    case 5:
      break;
    default:
      break;
  }

  if (activeStep.value < 5) {
    activeStep.value++;

    nextTick(() => {
      changingStep.value = false;
    });
  }
}

function onError() {
  savingChanges.value = false;
  dialogueModalOptions.value.loading = false;
  confirmationRef.value?.setSavingChanges(false);
  let error =
    "Your changes could not be saved. Please try again. If the issue persists, please contact our support team at {{phoneNumber}} or {{email}}.";

  error = resolveMergeFields(error, {
    phoneNumber: `<a class="underline link" :href="\`tel:${hrefTFN.value}\`">${tollFreeNumber.value}</a>`,
    email: `<a class="underline link" :href="\`mailto:${emailAddress}\`">${emailAddress.value}</a>`,
  });

  showDialogueModal.value = false;

  dialogueModalOptions.value = {
    description: error,
    closeBtn: true,
    buttons: [
      {
        label: "Ok",
        type: Number(ButtonTypes.Confirm),
        onClick: async () => {
          closeDialogueModal();
          savingChanges.value = false;
        },
      },
    ],
  };

  openDialogueModal(dialogueModalOptions.value);
}

function resetQueries() {
  router.push({
    query: {
      ...route.query, // Preserve existing queries
      rate: undefined, // Add/update the 'page' query
      step: undefined,
    },
  });
}

function handleResize() {
  if (window.innerWidth < 992) {
    stepMaxWidth.value = "100vw";
  } else {
    stepMaxWidth.value = activeStep.value === 1 ? "632px" : "959px";
  }
}

function updateRepaymentTerm(repaymentTermId: number) {
  repaymentTermSelectionRef.value?.selectUpdatedRepaymentTermOption(repaymentTermId);

  repaymentSummary.value.repaymentTermId = selectedRepaymentTermOption.value?.repaymentTermId || 0;

  repaymentSummary.value.repaymentTerm = selectedRepaymentTermOption.value?.repaymentTerm || "";

  repaymentSummary.value.interestRate = selectedRepaymentTermOption.value?.estimatedRateRanges
    ? "Range" + " " + selectedRepaymentTermOption.value?.estimatedRateRanges
    : "—";

  setSelectedRatePlan(ratePlanSelection.value);
}

function resetSelectedRepaymentTerm() {
  repaymentTermSelectionRef.value?.resetSelectedRepaymentTerm();

  repaymentSummary.value.repaymentTerm = "";

  repaymentSummary.value.repaymentTermId = 0;

  repaymentSummary.value.interestRate = "—";

  setSelectedRatePlan(undefined);
}

onMounted(async () => {
  window.addEventListener("resize", handleResize);

  loading.value = true;
  handleResize();
  await getRatePlansAsync();
  confirmationRef.value?.setSavingChanges(false);

  loading.value = false;
});

onUnmounted(() => {
  // remove the queries from the url
  resetQueries();

  // remove the event listener to prevent memory leaks
  window.removeEventListener("resize", handleResize);
});

watch(
  () => activeStep.value,
  (value: any) => {
    handleResize();
    if (preserveSelection.value) {
      router.push({
        query: {
          ...route.query, // Preserve existing queries
          step: value, // Add/update the 'step' query
        },
      });
    }

    if (value === 3) {
      // or if the new rate type does not offer the previously selected repayment term, reset it.
      if (
        repaymentSummary.value.repaymentTerm === "" ||
        repaymentSummary.value.repaymentTerm === undefined ||
        repaymentTermSelectionRef.value?.isValidRepaymentTerm === false
      ) {
        resetSelectedRepaymentTerm();
      }
    }

    if (value === 4) {
      // or if the new rate type and term does not offer the previously selected repayment type, reset it.
      if (
        repaymentSummary.value.repaymentType === "" ||
        repaymentSummary.value.repaymentType === undefined ||
        repaymentTypeSelectionRef.value?.isValidRepaymentType === false
      ) {
        repaymentTypeSelectionRef.value?.resetSelectedRepaymentType();
      }
    }
  },
);

__expose({
  closeModal,
  confirmationRef,
  loanConfigurationModule,
  rateTypeSelectionRef,
  repaymentSummaryMobileRef,
  repaymentSummaryRef,
  repaymentTermSelectionRef,
  repaymentTypeSelectionRef,
  selectedRepaymentTerm,
  selectedRepaymentType,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      ref_key: "modalRef",
      ref: modalRef,
      class: _normalizeClass(["pg-loan-configuration-modal medical-professional-grad no-min-height", { savingChanges: savingChanges.value }]),
      styleOptions: styleOptions.value,
      closeBtn: true,
      closeBtnHidden: savingChanges.value,
      footerStyle: 'center xs-pb-0 xs-pt-0',
      onClose: closeModal
    }, {
      footer: _withCtx(() => [
        (useMobileSizing.value && activeStep.value > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, " Selections are not final until process is completed. "))
          : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        (activeStep.value === 1)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["substep", ['subitem-1']])
            }, [
              _cache[1] || (_cache[1] = _createElementVNode("h1", { class: "title" }, "Selecting Your Repayment Type and Term", -1)),
              _cache[2] || (_cache[2] = _createElementVNode("p", null, " We'll walk you through a few simple steps to help you select your preferred repayment type and term. ", -1)),
              _createElementVNode("div", { class: "substep-footer" }, [
                _createElementVNode("button", {
                  onClick: next,
                  class: "next-btn"
                }, [...(_cache[0] || (_cache[0] = [
                  _createElementVNode("span", { class: "button-text" }, "Get Started", -1)
                ]))])
              ])
            ]))
          : _createCommentVNode("", true),
        (showCircleLoader.value)
          ? (_openBlock(), _createBlock(CircleLoader, { key: 1 }))
          : (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _withDirectives(_createElementVNode("div", {
                class: _normalizeClass(["substep", [
          `subitem-${activeStep.value}`,
          { singleRateType: rateTypeSelectionRef.value?.singleRateType && activeStep.value === 2 },
        ]]),
                style: {"text-align":"center"}
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(RepaymentSummary, {
                      ref_key: "repaymentSummaryMobileRef",
                      ref: repaymentSummaryMobileRef,
                      class: "mobile",
                      repaymentSummary: repaymentSummary.value,
                      isMobile: true,
                      ratePlan: ratePlanSelection.value
                    }, null, 8, ["repaymentSummary", "ratePlan"])
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _withDirectives(_createVNode(RateTypeSelection, {
                      ref_key: "rateTypeSelectionRef",
                      ref: rateTypeSelectionRef,
                      preserveSelection: preserveSelection.value
                    }, null, 8, ["preserveSelection"]), [
                      [_vShow, activeStep.value === 2]
                    ]),
                    _withDirectives(_createVNode(RepaymentTermSelection, {
                      ref_key: "repaymentTermSelectionRef",
                      ref: repaymentTermSelectionRef,
                      selectedRateTypeId: selectedRateType.value?.rateTypeId,
                      selectedRateType: selectedRateType.value
                    }, null, 8, ["selectedRateTypeId", "selectedRateType"]), [
                      [_vShow, activeStep.value === 3]
                    ]),
                    _withDirectives(_createVNode(RepaymentTypeSelection, {
                      ref_key: "repaymentTypeSelectionRef",
                      ref: repaymentTypeSelectionRef,
                      repaymentTerm: selectedRepaymentTermOption.value,
                      selectedRatePlan: selectedRatePlan.value
                    }, null, 8, ["repaymentTerm", "selectedRatePlan"]), [
                      [_vShow, activeStep.value === 4]
                    ]),
                    _createVNode(RepaymentSummary, {
                      ref_key: "repaymentSummaryRef",
                      ref: repaymentSummaryRef,
                      class: "desktop",
                      repaymentSummary: repaymentSummary.value,
                      ratePlan: ratePlanSelection.value
                    }, null, 8, ["repaymentSummary", "ratePlan"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  (activeStep.value > 1 && activeStep.value < 5)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(["button-container", {
              'justify-content-space-between': activeStep.value >= 2,
            }])
                      }, [
                        _withDirectives(_createElementVNode("button", {
                          class: "previous-btn",
                          onClick: goBack,
                          disabled: changingStep.value
                        }, [...(_cache[3] || (_cache[3] = [
                          _createElementVNode("span", { class: "button-text" }, "Back", -1)
                        ]))], 8, _hoisted_6), [
                          [_vShow, activeStep.value >= 2]
                        ]),
                        _createElementVNode("button", {
                          class: "next-btn",
                          onClick: next,
                          disabled: isNextButtonDisabled.value
                        }, [...(_cache[4] || (_cache[4] = [
                          _createElementVNode("span", { class: "button-text" }, "Continue", -1)
                        ]))], 8, _hoisted_7)
                      ], 2))
                    : _createCommentVNode("", true)
                ])
              ], 2), [
                [_vShow, activeStep.value > 1 && activeStep.value < 5]
              ])
            ])),
        _withDirectives(_createVNode(Confirmation, {
          ref_key: "confirmationRef",
          ref: confirmationRef,
          rateTypeSelectionRef: rateTypeSelectionRef.value,
          repaymentTermSelectionRef: repaymentTermSelectionRef.value,
          repaymentTypeSelectionRef: repaymentTypeSelectionRef.value,
          isCurrent: activeStep.value === 5,
          onPrevious: goBack,
          onComplete: complete
        }, null, 8, ["rateTypeSelectionRef", "repaymentTermSelectionRef", "repaymentTypeSelectionRef", "isCurrent"]), [
          [_vShow, activeStep.value === 5]
        ])
      ]),
      _: 1
    }, 8, ["class", "styleOptions", "closeBtnHidden"]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (_unref(showDialogueModal) === true)
        ? (_openBlock(), _createBlock(CustomDialogueModal, {
            key: 0,
            options: _unref(dialogueModalOptions),
            closeBtn: _unref(dialogueModalOptions).closeBtn,
            onClose: _unref(closeDialogueModal)
          }, null, 8, ["options", "closeBtn", "onClose"]))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})